import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Stack, Button, MenuItem, TextField, IconButton, Tooltip, Chip,
  Alert, CircularProgress, InputAdornment, Divider,
} from '@mui/material';
import {
  Add, DeleteOutline, CallSplit, ContentCopy, SaveOutlined, PostAddOutlined,
} from '@mui/icons-material';
import AccountSelect, { AccountOption } from '../../components/finance/AccountSelect';
import { budgetService, BudgetLineInput, BudgetStatus } from '../../services/budgetService';
import { financeService } from '../../services/financeService';
import { financeWorkspaceService } from '../../services/financeWorkspaceService';
import { hrService } from '../../services/hrService';
import { projectService } from '../../services/projectService';
import { formatIndianNumber } from '../../utils/currency';
import {
  P, cardSx, fieldSx, priBtn, outBtn, PX, FieldLabel, StatusPill, WorkspaceHeader,
  TotalsStrip, ValidationStrip,
} from '../../components/common/workspaceKit';

const MONTHS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];
const BUDGET_TYPES = ['OPERATING', 'CAPEX', 'PROJECT', 'CASHFLOW', 'REVENUE'];
const FY_OPTIONS = ['FY2025-26', 'FY2026-27', 'FY2027-28'];
const MONTH_W = 82;

interface EditLine {
  accountId: string; costCenterId: string; departmentId: string; projectId: string; months: number[];
}
const blankLine = (): EditLine => ({ accountId: '', costCenterId: '', departmentId: '', projectId: '', months: Array(12).fill(0) });
const lineTotal = (l: EditLine) => l.months.reduce((a, b) => a + (Number(b) || 0), 0);

// Full-page budget workspace — the modal (BudgetFormDialog) rebuilt onto the
// shared workspace pattern so a multi-line, month-phased budget has room to
// breathe. Routed at /finance/budgets/new and /finance/budgets/:id/edit.
const BudgetWorkspacePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const editId = id ? Number(id) : null;
  const isEdit = !!editId;

  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [costCenters, setCostCenters] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);

  const [name, setName] = useState('');
  const [financialYear, setFinancialYear] = useState('FY2026-27');
  const [budgetType, setBudgetType] = useState('OPERATING');
  const [owner, setOwner] = useState('');
  const [company, setCompany] = useState('');
  const [plant, setPlant] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<'DRAFT' | 'ACTIVE'>('DRAFT');
  const [loadedStatus, setLoadedStatus] = useState<BudgetStatus | null>(null);
  const [target, setTarget] = useState('');
  const [lines, setLines] = useState<EditLine[]>([blankLine()]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reference data.
  useEffect(() => {
    financeService.getAccounts({ isActive: true }).then((r: any) => setAccounts(r.accounts || [])).catch(() => {});
    financeWorkspaceService.listCostCenters().then((r) => setCostCenters(r.costCenters || [])).catch(() => {});
    hrService.getDepartments().then((r: any) => setDepartments(r.departments || [])).catch(() => {});
    projectService.getProjects().then((r: any) => setProjects(r || [])).catch(() => {});
  }, []);

  // Preload when editing.
  useEffect(() => {
    if (!editId) return;
    setLoading(true);
    budgetService.budget360(editId).then((b) => {
      setName(b.header.name); setFinancialYear(b.header.financialYear); setBudgetType(b.header.budgetType || 'OPERATING');
      setOwner(b.header.owner || ''); setCompany(b.header.company || ''); setPlant(b.header.plant || '');
      setNotes(b.header.notes || ''); setLoadedStatus(b.header.status);
      setStatus(b.header.status === 'ACTIVE' ? 'ACTIVE' : 'DRAFT');
      // Regroup stored monthly lines back into (account+dimensions) month grids.
      const map = new Map<string, EditLine>();
      for (const l of b.lines) {
        const key = `${l.accountId}|${l.costCenterId || ''}|${l.departmentId || ''}|${l.projectId || ''}`;
        if (!map.has(key)) {
          map.set(key, {
            accountId: String(l.accountId), costCenterId: l.costCenterId ? String(l.costCenterId) : '',
            departmentId: l.departmentId ? String(l.departmentId) : '', projectId: l.projectId ? String(l.projectId) : '',
            months: Array(12).fill(0),
          });
        }
        const el = map.get(key)!;
        if (l.periodNumber >= 1 && l.periodNumber <= 12) el.months[l.periodNumber - 1] += Number(l.amount) || 0;
        else for (let i = 0; i < 12; i += 1) el.months[i] += (Number(l.amount) || 0) / 12;
      }
      setLines(map.size ? Array.from(map.values()) : [blankLine()]);
      setError(null);
    }).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [editId]);

  const setLine = (i: number, patch: Partial<EditLine>) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const setMonth = (i: number, m: number, v: string) => setLines((ls) => ls.map((l, idx) => {
    if (idx !== i) return l; const months = [...l.months]; months[m] = Number(v) || 0; return { ...l, months };
  }));
  const spreadEven = (i: number, annual: number) => setLines((ls) => ls.map((l, idx) => {
    if (idx !== i) return l;
    const per = Math.round(annual / 12);
    const months = Array(12).fill(per);
    months[11] = annual - per * 11; // remainder to the last month
    return { ...l, months };
  }));
  const addLine = () => setLines((ls) => [...ls, blankLine()]);
  const duplicateLine = (i: number) => setLines((ls) => [...ls.slice(0, i + 1), { ...ls[i], months: [...ls[i].months] }, ...ls.slice(i + 1)]);
  const removeLine = (i: number) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : [blankLine()]));

  const allocated = useMemo(() => lines.reduce((a, l) => a + lineTotal(l), 0), [lines]);
  const monthTotals = useMemo(() => MONTHS.map((_, mi) => lines.reduce((a, l) => a + (Number(l.months[mi]) || 0), 0)), [lines]);
  const validLines = lines.filter((l) => l.accountId && lineTotal(l) > 0);
  const targetNum = Number(target) || 0;
  const difference = targetNum > 0 ? targetNum - allocated : 0;
  const reconcileBlocked = targetNum > 0 && Math.abs(difference) >= 1;
  const canSubmit = !!name && !!financialYear && validLines.length > 0 && !reconcileBlocked && !saving;

  const checks = [
    { ok: !!name, label: name ? 'Name set' : 'Name required' },
    { ok: !!financialYear, label: financialYear ? financialYear : 'Financial year required' },
    { ok: validLines.length > 0, label: validLines.length ? `${validLines.length} valid line${validLines.length === 1 ? '' : 's'}` : 'Add a line with account + amount' },
    { ok: !reconcileBlocked, label: targetNum > 0 ? (reconcileBlocked ? `Off target by ${formatIndianNumber(Math.abs(difference))}` : 'Reconciled to target') : 'No target set' },
  ];

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      const payloadLines: BudgetLineInput[] = [];
      for (const l of validLines) {
        l.months.forEach((amt, mi) => {
          if (amt > 0) {
            payloadLines.push({
              accountId: Number(l.accountId), amount: Number(amt), periodNumber: mi + 1,
              costCenterId: l.costCenterId ? Number(l.costCenterId) : null,
              departmentId: l.departmentId ? Number(l.departmentId) : null,
              projectId: l.projectId ? Number(l.projectId) : null,
            });
          }
        });
      }
      const body = { name, financialYear, budgetType, owner, company, plant, notes, lines: payloadLines };
      if (editId) {
        await budgetService.update(editId, body);
        navigate(`/finance/budgets/${editId}`);
      } else {
        const res = await budgetService.create({ ...body, status });
        const newId = res?.budget?.id;
        navigate(newId ? `/finance/budgets/${newId}` : '/finance/budgets');
      }
    } catch (e: any) { setError(e.message || 'Failed to save budget'); setSaving(false); }
  };

  const headerActions = (
    <>
      <Button variant="outlined" sx={outBtn} onClick={() => navigate(-1)} disabled={saving}>Cancel</Button>
      <Button variant="contained" sx={priBtn} onClick={submit} disabled={!canSubmit}
        startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : (isEdit ? <SaveOutlined sx={{ fontSize: 18 }} /> : <PostAddOutlined sx={{ fontSize: 18 }} />)}>
        {isEdit ? 'Save changes' : 'Create budget'}
      </Button>
    </>
  );

  return (
    <Box sx={{ bgcolor: P.bg, minHeight: '100%', pb: 6, color: P.text }}>
      <WorkspaceHeader
        onBack={() => navigate(-1)}
        title={isEdit ? 'Edit Budget' : 'New Budget'}
        subtitle="Plan account-level spend, phased across the 12 months of the financial year."
        chip={<StatusPill label={isEdit ? (loadedStatus || 'DRAFT') : status} tone={(isEdit ? loadedStatus : status) === 'ACTIVE' ? 'green' : 'draft'} />}
        actions={headerActions}
      />
      <ValidationStrip items={checks} />

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}><CircularProgress /></Box>
      ) : (
        <Box sx={{ px: PX, pt: 1.5 }}>
          {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

          {/* Totals */}
          <Box sx={{ mb: 2 }}>
            <TotalsStrip cells={[
              { label: 'Total Budget', value: `${formatIndianNumber(allocated)}`, tone: 'primary', sub: `${validLines.length} line${validLines.length === 1 ? '' : 's'}` },
              { label: 'Avg / Month', value: `${formatIndianNumber(Math.round(allocated / 12))}`, sub: 'evenly annualised' },
              { label: 'Peak Month', value: monthTotals.some((m) => m > 0) ? MONTHS[monthTotals.indexOf(Math.max(...monthTotals))] : '—', tone: 'amber', sub: monthTotals.some((m) => m > 0) ? `${formatIndianNumber(Math.max(...monthTotals))}` : 'no phasing yet' },
              targetNum > 0
                ? { label: 'Vs Target', value: `${formatIndianNumber(difference)}`, tone: Math.abs(difference) < 1 ? 'green' : 'red', sub: Math.abs(difference) < 1 ? 'reconciled' : 'must reconcile to post' }
                : { label: 'Budget Type', value: budgetType, sub: financialYear },
            ]} />
          </Box>

          {/* Header details */}
          <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0,1fr))' }, gap: '16px', p: '18px 20px' }}>
              <Box sx={{ gridColumn: { xs: '1 / -1', md: 'span 2' } }}><FieldLabel text="Budget Name" required /><TextField fullWidth size="small" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. FY26-27 Operating Plan" sx={fieldSx} /></Box>
              <Box><FieldLabel text="Financial Year" required /><TextField select fullWidth size="small" value={financialYear} onChange={(e) => setFinancialYear(e.target.value)} sx={fieldSx}>{FY_OPTIONS.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}</TextField></Box>
              <Box><FieldLabel text="Budget Type" required /><TextField select fullWidth size="small" value={budgetType} onChange={(e) => setBudgetType(e.target.value)} sx={fieldSx}>{BUDGET_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}</TextField></Box>
              <Box><FieldLabel text="Owner" /><TextField fullWidth size="small" value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="Budget owner" sx={fieldSx} /></Box>
              <Box><FieldLabel text="Company" /><TextField fullWidth size="small" value={company} onChange={(e) => setCompany(e.target.value)} sx={fieldSx} /></Box>
              <Box><FieldLabel text="Plant / Unit" /><TextField fullWidth size="small" value={plant} onChange={(e) => setPlant(e.target.value)} sx={fieldSx} /></Box>
              {!isEdit && <Box><FieldLabel text="Initial Status" /><TextField select fullWidth size="small" value={status} onChange={(e) => setStatus(e.target.value as any)} sx={fieldSx}><MenuItem value="DRAFT">Draft</MenuItem><MenuItem value="ACTIVE">Active</MenuItem></TextField></Box>}
              <Box><FieldLabel text="Reconcile Target" /><TextField fullWidth size="small" type="number" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="Optional" InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} sx={fieldSx} /></Box>
              <Box sx={{ gridColumn: { xs: '1 / -1', md: 'span 2' } }}><FieldLabel text="Notes" /><TextField fullWidth size="small" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Assumptions, drivers, approvals…" sx={fieldSx} /></Box>
            </Box>
          </Paper>

          {/* Lines */}
          <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1.75, borderBottom: `1px solid ${P.line}` }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 800, color: P.strong }}>Budget Lines <Box component="span" sx={{ color: P.muted, fontWeight: 600 }}>· {lines.length}</Box></Typography>
              <Button size="small" startIcon={<Add sx={{ fontSize: 18 }} />} sx={{ ...outBtn, height: 34 }} onClick={addLine}>Add line</Button>
            </Stack>

            <Stack spacing={1.5} sx={{ p: 2.5 }}>
              {lines.map((l, i) => {
                const total = lineTotal(l);
                return (
                  <Box key={i} sx={{ p: 1.5, borderRadius: '12px', border: `1px solid ${P.line}`, bgcolor: '#fcfdff' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1.3fr 1.3fr 1.3fr auto' }, gap: 1.25, alignItems: 'end', mb: 1.25 }}>
                      <Box><FieldLabel text="Account" required /><AccountSelect accounts={accounts} value={l.accountId} onChange={(v) => setLine(i, { accountId: v })} /></Box>
                      <Box><FieldLabel text="Cost Center" /><TextField select fullWidth size="small" value={l.costCenterId} onChange={(e) => setLine(i, { costCenterId: e.target.value })} sx={fieldSx}><MenuItem value="">—</MenuItem>{costCenters.map((cc) => <MenuItem key={cc.id} value={cc.id}>{cc.code} · {cc.name}</MenuItem>)}</TextField></Box>
                      <Box><FieldLabel text="Department" /><TextField select fullWidth size="small" value={l.departmentId} onChange={(e) => setLine(i, { departmentId: e.target.value })} sx={fieldSx}><MenuItem value="">—</MenuItem>{departments.map((d) => <MenuItem key={d.id} value={d.id}>{d.dept_name}</MenuItem>)}</TextField></Box>
                      <Box><FieldLabel text="Project" /><TextField select fullWidth size="small" value={l.projectId} onChange={(e) => setLine(i, { projectId: e.target.value })} sx={fieldSx}><MenuItem value="">—</MenuItem>{projects.map((p) => <MenuItem key={p.id} value={p.id}>{p.project_name || p.projectName}</MenuItem>)}</TextField></Box>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ pb: '2px' }}>
                        <SpreadControl onSpread={(v) => spreadEven(i, v)} />
                        <Tooltip title="Even-spread the line total across 12 months"><span><IconButton size="small" disabled={total <= 0} onClick={() => spreadEven(i, total)} sx={{ color: P.primary }}><CallSplit sx={{ fontSize: 18 }} /></IconButton></span></Tooltip>
                        <Tooltip title="Duplicate line"><IconButton size="small" onClick={() => duplicateLine(i)} sx={{ color: P.muted }}><ContentCopy sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                        <Tooltip title="Remove line"><IconButton size="small" onClick={() => removeLine(i)} sx={{ color: P.muted }}><DeleteOutline sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                      </Stack>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, overflowX: 'auto', pb: 0.5 }}>
                      {MONTHS.map((m, mi) => (
                        <Box key={m} sx={{ minWidth: MONTH_W }}>
                          <TextField size="small" type="number" label={m} value={l.months[mi] || ''} onChange={(e) => setMonth(i, mi, e.target.value)}
                            inputProps={{ style: { fontSize: 12.5, padding: '7px 7px', textAlign: 'right' } }} InputLabelProps={{ sx: { fontSize: 12 } }} sx={fieldSx} />
                        </Box>
                      ))}
                      <Box sx={{ minWidth: 130, display: 'flex', alignItems: 'center', pl: 1 }}>
                        <Chip size="small" label={`Σ ${formatIndianNumber(total)}`} sx={{ fontWeight: 800, bgcolor: P.primarySoft, color: P.primary, border: `1px solid ${P.primaryBorder}` }} />
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Stack>

            {/* Monthly totals across all lines */}
            <Divider sx={{ borderColor: P.line }} />
            <Box sx={{ px: 2.5, py: 1.75, bgcolor: P.head }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: P.muted, mb: 1 }}>Monthly Totals · all lines</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, overflowX: 'auto', alignItems: 'stretch' }}>
                {MONTHS.map((m, mi) => (
                  <Box key={m} sx={{ minWidth: MONTH_W, px: 0.75, py: 0.75, borderRadius: '8px', bgcolor: '#fff', border: `1px solid ${P.line}`, textAlign: 'right' }}>
                    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: P.muted, textTransform: 'uppercase' }}>{m}</Typography>
                    <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: monthTotals[mi] ? P.strong : P.ph }}>{monthTotals[mi] ? formatIndianNumber(monthTotals[mi]) : '—'}</Typography>
                  </Box>
                ))}
                <Box sx={{ minWidth: 130, px: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end' }}>
                  <Typography sx={{ fontSize: '10px', fontWeight: 700, color: P.muted, textTransform: 'uppercase' }}>Total</Typography>
                  <Typography sx={{ fontSize: '15px', fontWeight: 800, color: P.primary }}>{formatIndianNumber(allocated)}</Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

// Type an annual figure, hit split → evenly phased across 12 months.
const SpreadControl: React.FC<{ onSpread: (v: number) => void }> = ({ onSpread }) => {
  const [v, setV] = useState('');
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <TextField size="small" type="number" placeholder="Annual ₹" value={v} onChange={(e) => setV(e.target.value)}
        sx={{ width: 104, ...fieldSx }} inputProps={{ style: { fontSize: 12.5, padding: '7px 8px' } }} />
      <Tooltip title="Distribute this annual amount equally"><span>
        <IconButton size="small" disabled={!Number(v)} onClick={() => { onSpread(Number(v)); setV(''); }} sx={{ color: P.primary }}><CallSplit sx={{ fontSize: 18 }} /></IconButton>
      </span></Tooltip>
    </Stack>
  );
};

export default BudgetWorkspacePage;
